import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import Card from './common/Card';
import Button from './common/Button';

const CSVUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv'))) {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      toast.error('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/graduates/template/download', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'graduates_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setUploadResult(null);

    try {
      const response = await api.post('/graduates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Upload response:', response.data);
      setUploadResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        if (onUploadComplete) {
          // Wait a moment for the toast to show before closing
          setTimeout(() => {
            onUploadComplete();
            // Don't close modal automatically so user can see results
          }, 1000);
        }
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      const errorMsg = error.response?.data?.message || 'Error uploading CSV file';
      toast.error(errorMsg);
      setUploadResult({
        success: false,
        message: errorMsg,
        errors: [errorMsg]
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Import Graduates from CSV</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-lg text-sm">
            <h3 className="font-semibold text-blue-800 mb-2">Instructions:</h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Download the template CSV file below</li>
              <li>Fill in your graduate data (admin_number and name are required)</li>
              <li>Use department and course names as they appear in the system</li>
              <li>Date format: MM/DD/YYYY (e.g., 05/22/2024)</li>
              <li>Upload the filled CSV file</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="w-full sm:w-auto"
            >
              📥 Download CSV Template
            </Button>
          </div>

          <div className="border-t border-gray-200 my-4"></div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File:
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {file && (
              <p className="text-sm text-green-600 mt-2">
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex gap-3">
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1"
            >
              {uploading ? 'Uploading...' : '📤 Upload CSV'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
          </div>

          {/* Upload Results */}
          {uploadResult && (
            <div className={`p-4 rounded-lg mt-4 ${
              uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <h4 className={`font-semibold ${
                uploadResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                Upload Results:
              </h4>
              <div className="mt-2 space-y-1 text-sm">
                {uploadResult.stats && (
                  <>
                    <p>✅ Imported: <span className="font-semibold">{uploadResult.stats.imported || 0}</span></p>
                    <p>⏭️ Skipped: <span className="font-semibold">{uploadResult.stats.skipped || 0}</span></p>
                    <p>❌ Errors: <span className="font-semibold">{uploadResult.stats.errors || 0}</span></p>
                  </>
                )}
                {uploadResult.message && (
                  <p className="mt-2 font-medium">{uploadResult.message}</p>
                )}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-semibold text-red-700">Error Details:</p>
                    <ul className="list-disc list-inside text-red-600 max-h-40 overflow-y-auto">
                      {uploadResult.errors.map((error, index) => (
                        <li key={index} className="text-xs whitespace-pre-wrap">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadResult.results && uploadResult.results.length > 0 && (
                  <div className="mt-2">
                    <p className="font-semibold text-green-700">Successfully Imported:</p>
                    <ul className="list-disc list-inside text-green-600 max-h-40 overflow-y-auto">
                      {uploadResult.results.map((result, index) => (
                        <li key={index} className="text-xs">
                          {result.admin_number} - {result.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {uploadResult.success && (
                <Button
                  variant="outline"
                  onClick={() => {
                    onClose();
                    if (onUploadComplete) onUploadComplete();
                  }}
                  className="mt-3 w-full"
                >
                  Close & Refresh Data
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CSVUploadModal;
